import { HttpClient, HttpErrorResponse, HttpHeaders } from "@angular/common/http";
import { ErrorHandler, Injectable, NgZone } from "@angular/core";
import { Router } from "@angular/router";
import { lastValueFrom } from "rxjs";
import { ERROR_CODE, HTTP_STATUS, LinqException, constructLinqException, getHttpStatus, getUserMessage, isLinqException } from "../../common/exceptions";
import { ModelDetails, Organisation } from "../../common/organisation";
import { PROFILE_KEY, User } from "../../common/user";
import { environment } from "../../../environments/environment";
import { v4 } from "uuid";
import { ErrorDialogService } from "./errors/error-dialog.service";

// an error that happens every time the page redraws would otherwise report itself, and redraw, without end
const ERROR_BURST_LIMIT = 10
const ERROR_BURST_MS = 2000
// the same problem is reported to the server again only after a while, and a page reports only so many
const REPORT_AGAIN_MS = 5 * 60 * 1000
const MAX_REPORTS = 50

@Injectable({
    providedIn: 'root'
})
export class ExceptionAndLoggingService implements ErrorHandler {
    private reported = new Map<string, number>()        // when each problem was last reported
    private reports = 0
    private recentErrors:number[] = []                  // times of recent uncaught errors
    private quietened = false

	constructor(private router:Router, private http: HttpClient, private errorDialogService:ErrorDialogService, private zone: NgZone) {
	}

    // errors nothing else caught. A burst of them (usually one repeating as the page redraws) is only logged, so
    // showing it can't cause it again; it is shown once things are quiet
    async handleError(error: any) {
        const now = Date.now()
        this.recentErrors = this.recentErrors.filter(t => now - t < ERROR_BURST_MS)
        this.recentErrors.push(now)
        if (this.recentErrors.length > ERROR_BURST_LIMIT) {
            if (!this.quietened) console.error("The same errors keep happening; further ones are only logged until they stop", error)
            this.quietened = true
            return
        }
        this.quietened = false

        try {
            const linqException = await this.handleCaughtError(error, "Global error handler", "Unknown", "Other exception", this.getStoredUser())
            this.showErrorMessage(linqException)
        }catch(err) {
            // a problem here must not come back to this handler
            console.error("Error while handling an error", err, error)
        }
    }

    private getStoredUser():User|undefined {
        try {
            const jsonUser = localStorage.getItem(PROFILE_KEY)
            return jsonUser ? JSON.parse(jsonUser) : undefined
        }catch(err) {
            return undefined
        }
    }

    async handleCaughtError(error:any, component:string, service:string, internalMsg:string, user?:User, organisation?:Organisation, model?:ModelDetails, info?:any) {
        let linqException:LinqException
        if (error && isLinqException(error)) linqException = error as LinqException
        else if (error && error instanceof HttpErrorResponse) {
            console.error(error)
            linqException = isLinqException(error.error)?error.error:this.createLinqException(ERROR_CODE.ERR5, component, service, internalMsg, user, organisation, model, info, error)
            if (getHttpStatus(linqException) === HTTP_STATUS.UNAUTHORIZED) this.router.navigateByUrl("auth");
        }else if (error?.error && isLinqException(error.error)) {
            linqException = error.error as LinqException
        }else {
            linqException = this.createLinqException(ERROR_CODE.ERR7, component, service, internalMsg, user, organisation, model, info, error)
            console.error(error)
            if (this.shouldReport(linqException)) this.sendException(linqException)
        }

        return linqException
    }

    // tells the user what went wrong in plain words, keeping the technical details to one side for support
    showErrorMessage(linqException:LinqException) {
        console.error(linqException)
        const unexpected = linqException.msgCode === ERROR_CODE.ERR7 || getHttpStatus(linqException) === HTTP_STATUS.SERVER_ERROR
        const data = {
            message: getUserMessage(linqException) || 'Something unexpected happened in LINQ.',
            details: this.getErrorDetails(linqException),
            unexpected,
            // a fault in the page itself is usually cleared by reloading it
            canReload: linqException.msgCode === ERROR_CODE.ERR7,
        }
        // with a dialog already showing, the error is only counted on it: no need to redraw the page for that
        if (this.errorDialogService.isShowing) this.errorDialogService.openDialog(data)
        else this.zone.run(() => this.errorDialogService.openDialog(data));
    }

    private getErrorDetails(e:LinqException) {
        return [
            `Code: ERR${e.msgCode}${e.httpStatus ? ` (HTTP ${e.httpStatus})` : ''}`,
            e.component || e.service ? `Where: ${[e.component, e.service].filter(Boolean).join(' / ')}` : '',
            e.errorMessage ? `Error: ${e.errorMessage}` : '',
            e.organisationName ? `Organisation: ${e.organisationName}` : '',
            e.modelName ? `Model: ${e.modelName}` : '',
            `Version: ${e.version || environment.version}`,
            `Time: ${new Date().toISOString()}`,
        ].filter(Boolean).join('\n')
    }

    // each different problem once in a while; the same one again and again adds nothing
    private shouldReport(e:LinqException) {
        if (this.reports >= MAX_REPORTS) return false
        const key = [e.component, e.service, e.errorMessage].join('|')
        const now = Date.now()
        const last = this.reported.get(key)
        if (last !== undefined && now - last < REPORT_AGAIN_MS) return false
        this.reported.set(key, now)
        this.reports++
        return true
    }

    private createLinqException(msgCode:ERROR_CODE, component:string, service:string, internalMsg:string, user?:User, organisation?:Organisation, model?:ModelDetails, info?:any, error?:Error) {
        return constructLinqException(true, msgCode, component, service, internalMsg, environment.version, user, organisation, model, info, error)
    }

    private async sendException(exception:LinqException) {
        try {
            await lastValueFrom(this.http.post(`${environment.urlAddress}/api/logging/exceptions`, JSON.stringify(exception), {headers: new HttpHeaders({'Content-Type': 'application/json', 'x-client-token':v4()})}))
        }catch (err) {
            console.error("Exception while sending exception to server", err)
        }

    }

}